// src/CowTrackerDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Ensure this is correctly pointing to your backend API, including the /api path
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://buzzcam.media.mit.edu/api';

// Helper function for RSSI Quality
const getRssiQuality = (rssi) => {
  if (rssi === null || typeof rssi === 'undefined') {
    return { label: 'N/A', color: '#64748b' }; // Grey for N/A
  }
  if (rssi >= -70) return { label: 'Excellent', color: '#10b981' }; // Green
  if (rssi >= -90) return { label: 'Good', color: '#a3e635' }; // Lime green
  if (rssi >= -115) return { label: 'Fair', color: '#facc15' };   // Yellow
  return { label: 'Poor', color: '#ef4444' }; // Red
};

// Helper function for SNR Quality
const getSnrQuality = (snr) => {
  if (snr === null || typeof snr === 'undefined') {
    return { label: 'N/A', color: '#64748b' }; // Grey for N/A
  }
  if (snr >= 10) return { label: 'Excellent', color: '#10b981' };  // Green
  if (snr >= 5) return { label: 'Good', color: '#a3e635' };      // Lime green
  if (snr >= 0) return { label: 'Fair', color: '#facc15' };      // Yellow
  if (snr >= -5) return { label: 'Acceptable', color: '#f97316' };// Orange
  return { label: 'Poor', color: '#ef4444' };                   // Red
};

const celsiusToFahrenheit = (celsius) => {
  if (celsius === null || typeof celsius === 'undefined') return null;
  return (celsius * 9/5) + 32;
};

// Define the discrete time span options for the slider
const SPAN_OPTIONS = [
  { hours: 0.5, label: '30m', display: '30 minutes' },
  { hours: 1, label: '1h', display: '1 hour' },
  { hours: 6, label: '6h', display: '6 hours' },
  { hours: 24, label: '1d', display: '1 day' },
  { hours: 168, label: '1w', display: '1 week (7 days)' }, // 7*24
  { hours: 720, label: '1m', display: '1 month (30 days)' } // 30*24
];
const INITIAL_SPAN_INDEX = 0; // Default to '30m' 

const CowTrackerDashboard = ({ authToken, user, onLogout }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUids, setLoadingUids] = useState(false);
  const [availableUids, setAvailableUids] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [currentSpanIndex, setCurrentSpanIndex] = useState(INITIAL_SPAN_INDEX);
  const [selectedSpanHours, setSelectedSpanHours] = useState(SPAN_OPTIONS[INITIAL_SPAN_INDEX].hours);
  const [tempSpanHours, setTempSpanHours] = useState(24);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const refreshIntervalRef = useRef(null);
  const [lastKnownData, setLastKnownData] = useState(null);
  const [tempUnit, setTempUnit] = useState('C'); // 'C' or 'F'


  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pathLineRef = useRef(null);
  const markersLayerRef = useRef(null);

  const { username: currentUserName } = user || { username: 'User' };

  const getAnimalIcon = useCallback(() => '🐄', []);

  // Helper to get display temperature and unit string
  const getDisplayTemperature = useCallback((celsiusTemp) => {
    if (celsiusTemp === null || typeof celsiusTemp === 'undefined') {
      return 'N/A';
    }
    if (tempUnit === 'F') {
      return `${celsiusToFahrenheit(celsiusTemp).toFixed(1)}°F`;
    }
    return `${celsiusTemp.toFixed(1)}°C`;
  }, [tempUnit]);

const getSpanString = useCallback((hours) => { // Now using selectedSpanHours directly
    const option = SPAN_OPTIONS.find(opt => opt.hours === hours);
    if (option) { // Prefer direct label if it matches one of our options
        if (hours === 0.5) return '30m';
        if (hours === 1) return '1h';
        if (hours === 6) return '6h';
        if (hours === 24) return '1d';
        if (hours === 168) return '7d'; // Use 7d for consistency with Influx
        if (hours === 720) return '30d';// Use 30d for consistency with Influx
    }
    // Fallback for custom values if ever introduced, or if hours don't match SPAN_OPTIONS
    if (hours === 0.5) return '30m';
    if (hours < 24) return `${Math.round(hours)}h`; // Round for cleaner span strings
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days}d`;
    return `${days}d${remainingHours}h`;
  }, []); // This no longer depends on selectedSpanHours directly from state, but the value passed to it.

	 // This function is now for displaying the selected option's text
  const formatTimeRangeDisplay = useCallback(() => {
    return SPAN_OPTIONS[currentSpanIndex]?.display || `${selectedSpanHours} hours`;
  }, [currentSpanIndex, selectedSpanHours]);

  const formatTimeRange = (hours) => {
    if (hours === 0.5) return "30 minutes";
    if (hours === 1) return "1 hour";
    if (hours < 24) return `${hours} hours`;
    if (hours === 24) return "1 day";
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days} days`;
    return `${days} days ${remainingHours} hours`;
  };

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401 || response.status === 403) {
      console.error("Authentication token invalid or expired, logging out.");
      onLogout();
      throw new Error('Authentication failed');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `Request failed: ${response.status}` }));
      throw new Error(errorData.error || `API request failed: ${response.status}`);
    }
    return response.json();
  }, [authToken, onLogout]);

  const fetchAvailableUids = useCallback(async () => {
    if (!authToken) return;
    console.log(`[fetchAvailableUids] Triggered. Current selectedSpanHours: ${selectedSpanHours}, Current selectedUid: ${selectedUid}`);
    setLoadingUids(true);
    try {
      const spanString = getSpanString(selectedSpanHours);
      const result = await fetchWithAuth(`${API_BASE_URL}/uids?span=${spanString}`);
      if (Array.isArray(result)) {
        setAvailableUids(result);
        if (result.length > 0 && (!selectedUid || !result.includes(selectedUid))) {
          setSelectedUid(result[0]);
        } else if (result.length === 0) {
          setSelectedUid('');
        }
      } else {
        setAvailableUids([]);
        setSelectedUid('');
      }
    } catch (error) {
      console.error('Error fetching UIDs:', error.message);
      if (error.message !== 'Authentication failed') {
        setAvailableUids([]);
        setSelectedUid('');
        // Also clear data if UID selection process fails critically
        setData([]);
        setLastKnownData(null);
      }
    } finally {
      setLoadingUids(false);
    }
  }, [authToken, selectedSpanHours, fetchWithAuth, selectedUid]);

  const fetchData = useCallback(async () => {
    if (!selectedUid || !authToken) {
      setData([]);
      setLastKnownData(null);
      return;
    }
    setLoading(true);
    try {
      const spanString = getSpanString(selectedSpanHours);
      const result = await fetchWithAuth(`${API_BASE_URL}/data?uid=${selectedUid}&span=${spanString}`);
      if (Array.isArray(result)) {
        setData(result);
        setLastUpdated(new Date());
        setLastKnownData(result.length > 0 ? result[result.length - 1] : null);
      } else {
        setData([]);
        setLastKnownData(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error.message);
      if (error.message !== 'Authentication failed') {
        setData([]);
        setLastKnownData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedUid, selectedSpanHours, fetchWithAuth]);

  useEffect(() => {
    if (window.L_MAP_ASSETS_LOADED_FLAG) return;
    window.L_MAP_ASSETS_LOADED_FLAG = true; 

    let cssLoaded = !!document.getElementById('leaflet-css-link');
    let jsLoaded = !!window.L;

    if (!cssLoaded) {
      const link = document.createElement('link');
      link.id = 'leaflet-css-link';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/leaflet.css';
      document.head.appendChild(link);
    }

    if (!jsLoaded) {
      if (!document.getElementById('leaflet-js-script')) { 
        const script = document.createElement('script');
        script.id = 'leaflet-js-script';
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/leaflet.js';
        script.async = true;
        script.onload = () => {
          console.log("Leaflet JS explicitly loaded via script tag.");
        };
        document.head.appendChild(script);
      }
    }
  }, []);


  useEffect(() => {
    if (selectedUid && window.L && mapRef.current && !mapInstanceRef.current) {
      const map = window.L.map(mapRef.current, {
        center: [20, 0], 
        zoom: 2,
      });
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);
      mapInstanceRef.current = map;
      markersLayerRef.current = window.L.layerGroup().addTo(map);
    } else if (!selectedUid && mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      if (markersLayerRef.current) markersLayerRef.current = null; 
      if (pathLineRef.current) pathLineRef.current = null;
    }
    return () => {
      if (mapInstanceRef.current && !selectedUid) { 
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersLayerRef.current = null;
        pathLineRef.current = null;
      }
    };
  }, [selectedUid]); 


  useEffect(() => {
    const map = mapInstanceRef.current;
    const markersLayer = markersLayerRef.current;
    if (!map || !window.L || !markersLayer || !selectedUid || !data) {
        if (map && markersLayer && pathLineRef.current) {
            map.removeLayer(pathLineRef.current);
            pathLineRef.current = null;
        }
        if (map && markersLayer) {
            markersLayer.clearLayers();
        }
        return;
    }
    map.invalidateSize();
    if (pathLineRef.current) {
      map.removeLayer(pathLineRef.current);
      pathLineRef.current = null;
    }
    markersLayer.clearLayers();

    if (data.length === 0) return;
    const validDataPoints = data.filter(p => typeof p.lat === 'number' && typeof p.lon === 'number' && p.lat !== 0 && p.lon !== 0);
    if (validDataPoints.length === 0) return;

    const pathCoordinates = validDataPoints.map(point => [point.lat, point.lon]);
    const newPathLine = window.L.polyline(pathCoordinates, {
      color: '#06b6d4', weight: 3, opacity: 0.8, smoothFactor: 1
    }).addTo(map);
    pathLineRef.current = newPathLine;

    const getDisplayPoints = (points) => {
      if (points.length <= 50) return points;
      if (points.length <= 200) return points.filter((_, index) => index % 2 === 0);
      if (points.length <= 500) return points.filter((_, index) => index % 5 === 0);
      return points.filter((_, index) => index % 10 === 0);
    };

    let displayableMapPoints = getDisplayPoints(validDataPoints);
    const lastActualPoint = validDataPoints[validDataPoints.length - 1];
    if (lastActualPoint && !displayableMapPoints.find(p => p.time === lastActualPoint.time)) {
        displayableMapPoints.push(lastActualPoint);
    }
    displayableMapPoints = Array.from(new Set(displayableMapPoints.map(p => p.time)))
                             .map(time => displayableMapPoints.find(p => p.time === time));

    displayableMapPoints.forEach((point) => {
      const isLast = point.time === lastActualPoint?.time;
      const originalIndex = validDataPoints.findIndex(dp => dp.time === point.time);
      const opacity = Math.max(0.3, (originalIndex + 1) / validDataPoints.length);
      const displayTempStr = getDisplayTemperature(point.temp);

      if (isLast) {
        const animalIcon = window.L.divIcon({ 
            html: getAnimalIcon(), 
            iconSize: [40, 40], // Increased iconSize for clickable area
            className: 'animal-icon' 
        });
        window.L.marker([point.lat, point.lon], { icon: animalIcon })
          .bindPopup(`
            <div style="background: linear-gradient(135deg, #1e293b 0%, #334155 100%); padding: 20px; border-radius: 12px; color: #f1f5f9; border: 1px solid #475569;">
              <div style="color: #06b6d4; font-weight: 600; margin-bottom: 12px;">🎯 Current Position</div>
              <div style="font-size: 13px; line-height: 1.6;">
                <div><strong>📅 Time:</strong> ${new Date(point.time).toLocaleString()}</div>
                <div><strong>🔋 Battery:</strong> ${point.battery_percentage?.toFixed(1) || 'N/A'}%</div>
                <div><strong>💨 PM2.5:</strong> ${point.pm2_5 ?? 'N/A'} μg/m³</div>
                <div><strong>☀️ Light:</strong> ${point.light ?? 'N/A'} lux</div>
                <div><strong>🌡️ Temp:</strong> ${displayTempStr} || 'N/A'}°C / 💧 Hum: ${point.hum?.toFixed(1) || 'N/A'}%</div>
                <div><strong>🌊 Pressure:</strong> ${point.pres?.toFixed(0) || 'N/A'} Pa</div>
                <div><strong>📶 RSSI:</strong> ${point.rssi ?? 'N/A'} dBm / 📡 SNR: ${point.snr?.toFixed(1) || 'N/A'} dB</div>
              </div>
            </div>
          `)
          .addTo(markersLayer);
      } else {
        const size = Math.max(4, opacity * 8);
        const timeAgo = new Date() - new Date(point.time);
        const hoursAgo = timeAgo / (1000 * 60 * 60);
        let color = '#64748b';
        if (hoursAgo < 1) color = '#06b6d4'; else if (hoursAgo < 6) color = '#0ea5e9'; else if (hoursAgo < 24) color = '#0284c7'; else color = '#0369a1';
        window.L.circleMarker([point.lat, point.lon], { radius: size, fillColor: color, color: color, weight: 2, opacity: opacity, fillOpacity: opacity * 0.9 })
          .bindPopup(`
            <div style="padding: 16px; border-radius: 8px; background: #1e293b; color: #f1f5f9; border: 1px solid #475569;">
              <div style="color: #06b6d4; font-weight: 600; margin-bottom: 8px;">📍 Historical Position</div>
              <div style="font-size: 13px;">
                <div><strong>📅 Time:</strong> ${new Date(point.time).toLocaleString()}</div>
                <div><strong>⏰ ${hoursAgo < 1 ? Math.round(hoursAgo * 60) + ' min' : Math.round(hoursAgo) + ' hours'} ago</strong></div>
                <div><strong>🔋 Battery:</strong> ${point.battery_percentage?.toFixed(1) || 'N/A'}%</div>
                <div><strong>🌡️ Temp:</strong>  ${displayTempStr}  / 💧 Hum: ${point.hum?.toFixed(1) || 'N/A'}%</div>
              </div>
            </div>
          `)
          .addTo(markersLayer);
      }
    });

    if (validDataPoints.length > 1) {
      const arrowSpacing = Math.max(1, Math.floor(validDataPoints.length / 10));
      for (let i = arrowSpacing; i < validDataPoints.length; i += arrowSpacing) {
        const start = validDataPoints[i - arrowSpacing];
        const end = validDataPoints[i];
        if (!start || !end) continue;
        const bearing = Math.atan2(end.lon - start.lon, end.lat - start.lat) * 180 / Math.PI;
        const arrowIcon = window.L.divIcon({ html: `<div style="transform: rotate(${bearing}deg); font-size: 14px; color: #0891b2;">➤</div>`, iconSize: [18, 18], className: 'direction-arrow' });
        window.L.marker([end.lat, end.lon], { icon: arrowIcon }).addTo(markersLayer);
      }
    }
    if (pathCoordinates.length > 0) {
      try {
        map.fitBounds(newPathLine.getBounds(), { padding: [30, 30], animate: false });
      } catch (e) {
        if (lastActualPoint) map.setView([lastActualPoint.lat, lastActualPoint.lon], map.getZoom() || 15);
      }
    } else if (validDataPoints.length === 1) {
      map.setView([validDataPoints[0].lat, validDataPoints[0].lon], 15);
    }
  }, [data, selectedUid, getAnimalIcon, getDisplayTemperature, tempUnit]);

  useEffect(() => {
    console.log("[useEffect for UIDs] Triggered. AuthToken present:", !!authToken);
    if (authToken) { // Only fetch if logged in
      fetchAvailableUids();
    }
  }, [fetchAvailableUids, authToken]); // authToken ensures it runs on login/logout
	
  useEffect(() => {
    console.log("[useEffect for Data] Triggered. selectedUid:", selectedUid, "authToken present:", !!authToken);
    if (selectedUid && authToken) { 
      fetchData(); 
    } else { setData([]); setLastKnownData(null); }
  }, [selectedUid, fetchData, authToken]); // Added authToken

  useEffect(() => {
    if (autoRefresh && selectedUid) {
      refreshIntervalRef.current = setInterval(fetchData, 30000);
      return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
    } else if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, [autoRefresh, selectedUid, fetchData]);

  const handleSliderIndexChange = (event) => {
    setCurrentSpanIndex(parseInt(event.target.value, 10));
  };

  const handleSliderRelease = () => {
    const newSelectedHours = SPAN_OPTIONS[currentSpanIndex].hours;
    // Only update and refetch if the actual hour value has changed
    if (newSelectedHours !== selectedSpanHours) {
        const previousSelectedSpanHours = selectedSpanHours;
        setSelectedSpanHours(newSelectedHours);
        console.log(`Time range changed to: ${SPAN_OPTIONS[currentSpanIndex].label} (${newSelectedHours}h)`);

        if (Math.abs(newSelectedHours - previousSelectedSpanHours) > 24 * 7) { 
          console.log("Significant span change, resetting selected UID.");
          setSelectedUid(''); 
        }
    }
  };
	
  const handleSpanHoursChange = (newHours) => {
    setSelectedSpanHours(newHours);
    setTempSpanHours(newHours);
    // The selectedSpanHours used in the condition below will be the one from the previous render,
    // which is correct for comparing against newHours.
    if (Math.abs(newHours - selectedSpanHours) > 24 * 7) { 
      console.log("Significant span change detected in handleSpanHoursChange, resetting selected UID.");
      setSelectedUid(''); 
    }
  }; // fetchAvailableUids will be re-triggered by its own useEffect due to selectedSpanHours change
 


  const formatMultipleFields = useCallback((fields) => data.map((point, index) => ({
    time: new Date(point.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    fullTime: new Date(point.time).toLocaleString(), index: index,
    ...fields.reduce((acc, fld) => ({ ...acc, [fld]: point[fld] === null || typeof point[fld] === 'undefined' ? 0 : point[fld] }), {}),
    originalPoint: point
  })), [data]);

  const CustomTooltip = ({ active, payload }) => { /* ... as before ... */ };

  const formatChartData = useCallback((field) => {
    return data.map((point, index) => {
      let value = point[field] === null || typeof point[field] === 'undefined' ? null : point[field]; // Keep null for conversion check
      if (field === 'temp' && tempUnit === 'F' && typeof value === 'number') {
        value = celsiusToFahrenheit(value);
      }
      return {
        time: new Date(point.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        fullTime: new Date(point.time).toLocaleString(),
        value: (value === null || typeof value === 'undefined') ? 0 : value, // Default to 0 for chart if still null
        index: index, originalPoint: point
      };
    });
  }, [data, tempUnit]); // Added tempUnit

  const toggleTempUnit = () => {
    setTempUnit(prevUnit => prevUnit === 'C' ? 'F' : 'C');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 75%, #475569 100%)', padding: '16px', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: '100%', margin: '0 auto', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', padding: '0 16px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(1.8rem, 6vw, 2.8rem)', fontWeight: '800', background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '8px', letterSpacing: '-0.02em' }}>CollarID 🐄</h1>
            <p style={{ fontSize: 'clamp(0.8rem, 3vw, 1.1rem)', color: '#94a3b8', fontWeight: '400', letterSpacing: '0.05em', margin: 0 }}>Advanced Animal Tracking & Environmental Monitoring</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ color: '#e2e8f0', fontSize: '0.9rem', marginRight: '16px' }}>Welcome, <strong style={{ color: '#06b6d4' }}>{currentUserName}</strong></span>
            <button onClick={onLogout} style={{ padding: '10px 20px', fontSize: '0.9rem', fontWeight: '600', color: '#f1f5f9', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease-in-out' }} onMouseOver={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = '#06b6d4';}} onMouseOut={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(148,163,184,0.3)';}}>Logout</button>
          </div>
        </div>

        {/* Control Panel */}
        {/* ... Your existing Control Panel JSX ... */}
         <div style={{ background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.6) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '24px', marginBottom: '48px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>Device Selection</label>
                    <select value={selectedUid} onChange={(e) => setSelectedUid(e.target.value)} disabled={loadingUids || availableUids.length === 0}
                        style={{ width: '100%', padding: '16px 20px', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '16px', outline: 'none', fontSize: '15px', background: 'rgba(30,41,59,0.8)', color: '#f1f5f9', cursor: (loadingUids || availableUids.length === 0) ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', backdropFilter: 'blur(10px)', minHeight: '52px' }}>
                        <option value="">{loadingUids ? 'Loading devices...' : (availableUids.length === 0 ? 'No devices available' : 'Select a device')}</option>
                        {availableUids.map(uid => (<option key={uid} value={uid} style={{ background: '#1e293b' }}>CollarID {uid}</option>))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>Time Range</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ padding: '0 8px' }}>
                            <input 
                                type="range" 
                                min="0" 
                                max={SPAN_OPTIONS.length - 1} 
                                step="1" 
                                value={currentSpanIndex} 
                                onChange={handleSliderIndexChange} 
                                onMouseUp={handleSliderRelease} 
                                onTouchEnd={handleSliderRelease} 
                                style={{ width: '100%', height: '8px', borderRadius: '8px', background: 'linear-gradient(to right, #06b6d4, #0891b2)', outline: 'none', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }} 
                            />
                        </div>
                        {/* Static labels for guidance, slider snaps to these points */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8', padding: '0 8px' }}>
                            {SPAN_OPTIONS.map(opt => <span key={opt.label}>{opt.label}</span>)}
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '0.9rem', fontWeight: '600', color: '#06b6d4' }}>
                            {formatTimeRangeDisplay()} {/* Display the selected option's text */}
                        </div>
                    </div>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>Settings</label>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#e2e8f0' }}>Auto-Refresh</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}><input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} /><span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: autoRefresh ? '#06b6d4' : '#64748b', transition: 'all 0.3s ease', borderRadius: '24px' }}><span style={{ position: 'absolute', content: "''", height: '18px', width: '18px', left: autoRefresh ? '26px' : '3px', bottom: '3px', backgroundColor: 'white', transition: 'all 0.3s ease', borderRadius: '50%' }} /></span></label>
                            <span style={{ fontSize: '0.875rem', color: autoRefresh ? '#06b6d4' : '#94a3b8', fontWeight: '500' }}>{autoRefresh ? 'On (30s)' : 'Off'}</span>
                        </div>
                    </div>
	            {/* Temperature Unit Toggle */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#e2e8f0' }}>Temperature Unit</span>
                        <button
                            onClick={toggleTempUnit}
                            style={{ padding: '8px 12px', fontSize: '0.85rem', fontWeight: '500', color: '#f1f5f9', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '10px', cursor: 'pointer'}}
                        >
                           Show in °{tempUnit === 'C' ? 'F' : 'C'}
                        </button>
                    </div>
                    {lastUpdated && selectedUid && (<div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8', padding: '8px', backgroundColor: 'rgba(51,65,85,0.3)', borderRadius: '8px', marginBottom: '16px' }}>Last updated: {lastUpdated.toLocaleTimeString()}</div>)}
                    <button onClick={() => { fetchAvailableUids().then(() => { if(selectedUid) fetchData(); });}} disabled={loading || loadingUids || !authToken} style={{ width: '100%', background: (loading || loadingUids) ? 'linear-gradient(135deg, #64748b, #475569)' : 'linear-gradient(135deg, #06b6d4, #0891b2)', color: 'white', padding: '16px 24px', borderRadius: '16px', border: 'none', cursor: (loading || loadingUids || !authToken) ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '600', transition: 'all 0.3s ease', boxShadow: '0 10px 25px rgba(6,182,212,0.3)', minHeight: '52px' }}>{loading || loadingUids ? 'Updating...' : '🔄 Manual Refresh'}</button>
                </div>
            </div>
        </div>

        {/* Status Cards */}
        {lastKnownData && selectedUid ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>📍</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>Location</span></div>
                <div style={{fontSize:'1.1rem',fontWeight:'600',color:'#06b6d4',marginBottom:'8px'}}>{lastKnownData.lat?.toFixed(6) || 'N/A'}, {lastKnownData.lon?.toFixed(6) || 'N/A'}</div>
                <div style={{fontSize:'0.8rem',color:'#94a3b8'}}>Last update: {new Date(lastKnownData.time).toLocaleString()}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>🔋</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>Battery Life</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700',color:'#10b981'}}>{typeof lastKnownData.battery_percentage === 'number' ? `${lastKnownData.battery_percentage.toFixed(1)}%` : 'N/A'}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>💨</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>PM2.5 Air Quality</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700',color:'#f59e0b'}}>{lastKnownData.pm2_5 ?? 'N/A'} μg/m³</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>🌡️</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>Temp & Humidity</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700',color:'#ef4444'}}>
                    {getDisplayTemperature(lastKnownData.temp)}
                    {typeof lastKnownData.hum === 'number' && 
                        <span style={{color: '#60a5fa', marginLeft: '8px', fontSize: '1.25rem'}}> / {lastKnownData.hum.toFixed(1)}%</span>
                    }
                </div>
            </div>
		<div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>☀️</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>Light Level</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700',color:'#eab308'}}>{lastKnownData.light ?? 'N/A'} lux</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px',background:'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>🌊</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>Atmospheric Pressure</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700',color:'#8b5cf6'}}>{lastKnownData.pres?.toFixed(0) || 'N/A'} Pa</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px', background: getRssiQuality(lastKnownData.rssi).color }}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>📶</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>RSSI</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700', color: getRssiQuality(lastKnownData.rssi).color }}>{lastKnownData.rssi !== null && typeof lastKnownData.rssi !== 'undefined' ? `${lastKnownData.rssi} dBm` : 'N/A'}</div>
                <div style={{fontSize:'0.8rem', color: getRssiQuality(lastKnownData.rssi).color, marginTop: '4px' }}>{getRssiQuality(lastKnownData.rssi).label}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative', overflow: 'hidden' }}>
                <div style={{position:'absolute',top:0,left:0,right:0,height:'3px', background: getSnrQuality(lastKnownData.snr).color }}/>
                <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}><span style={{fontSize:'1.5rem'}}>📡</span><span style={{fontSize:'1rem',fontWeight:'600',color:'#f1f5f9'}}>SNR</span></div>
                <div style={{fontSize:'1.75rem',fontWeight:'700', color: getSnrQuality(lastKnownData.snr).color }}>{lastKnownData.snr !== null && typeof lastKnownData.snr !== 'undefined' ? `${lastKnownData.snr.toFixed(1)} dB` : 'N/A'}</div>
                 <div style={{fontSize:'0.8rem', color: getSnrQuality(lastKnownData.snr).color, marginTop: '4px' }}>{getSnrQuality(lastKnownData.snr).label}</div>
            </div>
          </div>
        ) : ( selectedUid && !loading && !loadingUids && <div style={{textAlign: 'center', color:'#94a3b8', padding: '50px'}}>No current data available for this device. Try adjusting the time range.</div>)}

        {/* Map */}
        {selectedUid && ( <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', marginBottom: '48px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}> <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#f1f5f9', marginBottom: '24px', textAlign: 'center' }}>Where in the World? 🌍</h2> <div ref={mapRef} style={{ width: '100%', height: '500px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', background: '#1a2332' }} /> {data.length === 0 && selectedUid && !loading && <p style={{textAlign: 'center', color: '#94a3b8', marginTop: '20px'}}>No location data to display for the selected period.</p>} </div> )}

        {/* Charts Grid */}
        {selectedUid && data.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', // MODIFIED HERE for wider plots on desktop
            gap: '24px', 
            minWidth: 0 
          }}>
            {/* PM Chart */}
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>💨</span>Particulate Matter (μg/m³)</h3>
                <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={formatMultipleFields(['pm1', 'pm2_5', 'pm10'])} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="pm1" stroke="#06b6d4" name="PM1" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="pm2_5" stroke="#0891b2" name="PM2.5" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="pm10" stroke="#0e7490" name="PM10" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* NEW Humidity Chart */}
            {<div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>💧</span>Relative Humidity (%)</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('hum')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} domain={['auto', 'auto']} unit="%"/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#2dd4bf" name="Humidity (%)" strokeWidth={2} dot={false} /> {/* Teal color for humidity */}
                    </LineChart>
                </ResponsiveContainer>
            </div>
	    /* Light Chart */}
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>☀️</span>Light Levels (Lux)</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('light')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} domain={['auto', 'auto']}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}} />
                        <Line type="monotone" dataKey="value" stroke="#eab308" name="Light (lux)" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* Temperature Chart */}
             <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>🌡️</span>Temperature (°{tempUnit})</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('temp')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} domain={['auto', 'auto']} unit={`°${tempUnit}`}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#ef4444" name={`Temp (°${tempUnit})`} strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
		{/* Gas Chart */}
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>🌬️</span>Gas Sensor</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('gas')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} domain={['auto', 'auto']}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#8b5cf6" name="Gas" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* Pressure Chart */}
            <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>🌊</span>Atmospheric Pressure (Pa)</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('pres')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis stroke="#94a3b8" fontSize={12} domain={['auto', 'auto']} unit=" Pa"/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#38bdf8" name="Pressure (Pa)" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* Battery Chart (Full Width) */}
            <div style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <h3 style={{fontSize:'1.5rem',fontWeight:'700',color:'#f1f5f9',marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px'}}><span>🔋</span>Battery Life (%)</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={formatChartData('battery_percentage')} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="2 2" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} interval="preserveStartEnd" tickFormatter={(tick) => tick.split(' ')[0]}/>
                        <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} unit="%"/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{fontSize: "12px", paddingTop: '10px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#10b981" name="Battery %" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
          </div>
        ) : ( selectedUid && !loading && !loadingUids && <div style={{textAlign: 'center', color:'#94a3b8', padding: '50px'}}>No data available to display charts for this device and period.</div>)}

        {/* Data Summary */}
        {selectedUid && data.length > 0 && (
          <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(51,65,85,0.7) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', marginTop: '48px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', textAlign: 'center' }}>
            <div style={{ color: '#f1f5f9', fontSize: '1.2rem', fontWeight: '500', lineHeight: '1.8' }}>
              📊 Displaying <span style={{ color: '#06b6d4', fontWeight: '700' }}>{data.length}</span> data points for CollarID <span style={{color: '#06b6d4', fontWeight: '700'}}>{selectedUid}</span>
              <br />
              <span style={{ fontSize: '1rem', color: '#94a3b8' }}>
                Journey: {new Date(data[0].time).toLocaleString()} → {new Date(data[data.length - 1].time).toLocaleString()}
              </span>
            </div>
          </div>
        )}
         {!selectedUid && !loadingUids && !loading && <div style={{textAlign: 'center', color:'#94a3b8', padding: '50px', fontSize: '1.2rem'}}>Please select a device to view data. If no devices are listed, try adjusting the time range.</div>}

        {/* Footer */}
               <div style={{ background: 'linear-gradient(135deg, rgba(30,41,59,0.8) 0%, rgba(51,65,85,0.6) 100%)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '32px', marginTop: '48px', border: '1px solid rgba(148,163,184,0.2)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', textAlign: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', fontSize: '1rem', color: '#94a3b8' }}>
            <div><a href="https://www.media.mit.edu/projects/collarid/overview/" target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', textDecoration: 'none', fontWeight: '600', fontSize: '1.1rem', transition: 'color 0.2s' }}>📡 CollarID Project Page</a></div>
            <div>Created by <a href="https://patrickchwalek.com" target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', textDecoration: 'none', fontWeight: '600', transition: 'color 0.2s' }}>Patrick Chwalek</a></div>
          </div>
        </div>
      </div>
      <style>{`
        .animal-icon { 
            background: none !important; 
            border: none !important; 
            font-size: 32px; /* MODIFIED for larger cow */
            text-align: center; 
            filter: drop-shadow(2px 2px 8px rgba(0,0,0,0.4)); 
        }
        .direction-arrow { background: none !important; border: none !important; text-align: center; }
        input[type="range"]::-webkit-slider-thumb { appearance: none; height: 20px; width: 20px; border-radius: 50%; background: linear-gradient(135deg, #06b6d4, #0891b2); cursor: pointer; box-shadow: 0 4px 12px rgba(6,182,212,0.4); border: 2px solid #f1f5f9; margin-top: -6px; }
        input[type="range"]::-moz-range-thumb { height: 16px; width: 16px; border-radius: 50%; background: linear-gradient(135deg, #06b6d4, #0891b2); cursor: pointer; box-shadow: 0 4px 12px rgba(6,182,212,0.4); border: 2px solid #f1f5f9; }
        select:focus, input[type="text"]:focus, input[type="password"]:focus, input[type="checkbox"]:focus { border-color: #06b6d4 !important; box-shadow: 0 0 0 3px rgba(6,182,212,0.1) !important; }
        button:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.1); }
        .recharts-legend-item { font-size: 12px !important; margin-right: 10px !important; }
        a:hover { color: #38bdf8 !important; text-decoration: underline !important; }
        .leaflet-popup-content-wrapper {  background: #1e293b; color: #f1f5f9; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); }
        .leaflet-popup-content { margin: 13px 19px; font-size: 13px; line-height: 1.6; }
        .leaflet-popup-tip { background: #1e293b; }
        .leaflet-control-attribution a { color: #06b6d4; }
      `}</style>
    </div>
  );
};

export default CowTrackerDashboard;
